import { Link } from 'react-router';
import { paths } from '@/config/paths';
import { formatIsoDateTimeOffset, formatMinorUnits, formatSqlDateTime, isGuid } from '@/shared/lib';
import { Guid } from '@/shared/ui';
import { valueText } from '../model/records';
import type { AnalyticsColumn } from '../model/schema';

/** Values longer than this are truncated in tables and collapsed on record pages. */
const LONG_TEXT = 80;
const PREVIEW_LENGTH = 120;

interface CellValueProps {
  column: AnalyticsColumn;
  value: unknown;
  /** `cell`: one line for tables; `detail`: expandable full value for record pages. */
  variant?: 'cell' | 'detail';
  /** The row's currency (Stripe `Currency`): `…Minor` amounts then show as money, e.g. €30,773.40. */
  currency?: string | null | undefined;
}

/** Pretty-prints JSON objects/arrays stored as text; other text is returned unchanged. */
function prettyText(text: string): string {
  if (!/^\s*[[{]/.test(text)) return text;
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

/** Only http(s) URLs become links, so stored `javascript:` or `data:` values stay inert. */
function isWebUrl(text: string): boolean {
  if (!/^https?:\/\//i.test(text)) return false;
  try {
    return ['http:', 'https:'].includes(new URL(text).protocol);
  } catch {
    return false;
  }
}

/** Renders one reporting value according to its column kind. */
export function CellValue({ column, value, variant = 'cell', currency }: CellValueProps) {
  if (value === null || value === undefined) return <span className="cell-null">—</span>;
  if (typeof value === 'boolean') return <>{value ? 'Yes' : 'No'}</>;

  const text = valueText(value);
  if (currency && column.kind === 'integer' && column.name.endsWith('Minor')) {
    const money = formatMinorUnits(text, currency);
    if (money) {
      return (
        <span className="cell-number" title={`${text} (minor units)`}>
          {money}
        </span>
      );
    }
  }
  switch (column.kind) {
    case 'datetime':
      return <time dateTime={text}>{formatSqlDateTime(text)}</time>;
    case 'datetimeoffset':
      return <time dateTime={text}>{formatIsoDateTimeOffset(text)}</time>;
    case 'date':
      // SQL date values arrive as midnight date-times ("2026-01-01T00:00:00").
      return <time dateTime={text.slice(0, 10)}>{text.slice(0, 10)}</time>;
    case 'integer':
    case 'decimal':
    case 'float':
      // Integer keys (e.g. EventTypeId) link to their record like text IDs do.
      return column.ref ? (
        <Link className="cell-number" to={paths.analytics.record(column.ref.key, text)}>
          {text}
        </Link>
      ) : (
        <span className="cell-number">{text}</span>
      );
    default:
      break;
  }
  // Grids show GUIDs shortened (full value on hover); record pages show them in full.
  const guid = variant === 'cell' && isGuid(text);
  if (column.ref && text) {
    return (
      <Link to={paths.analytics.record(column.ref.key, text)}>
        {guid ? <Guid value={text} /> : text}
      </Link>
    );
  }
  if (guid) return <Guid value={text} />;
  if (text === '') return <span className="cell-null">(empty)</span>;
  if (isWebUrl(text)) {
    return (
      <a href={text} target="_blank" rel="noopener noreferrer" className="cell-url" title={text}>
        {text}
      </a>
    );
  }
  if (text.length <= LONG_TEXT) return <>{text}</>;

  if (variant === 'detail') {
    return (
      <details className="long-value">
        <summary>
          {text.slice(0, PREVIEW_LENGTH)}… <span className="muted">({text.length} characters)</span>
        </summary>
        <pre>{prettyText(text)}</pre>
      </details>
    );
  }
  return (
    <span className="cell-text" title={text}>
      {text}
    </span>
  );
}
